import { Container, Header, Layout } from '@/components/SettingsLayout';
import { Text, Divider, Button } from '@/ui';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import SkeletonTable from '@/components/SkeletonLoader/SkeletonTable';
import { useGetUser } from '@/hooks';
import {
  useGetCurrentPlatformReportQuery,
  useGetPlatformBillingByReportQuery,
} from '@/store/slices/pricingSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useState, useMemo } from 'react';
import { debounce } from '@/utils/helpers/debounce';
import CurrentResiduals from './CurrentResiduals';
import ResidualHistory from './ResidualHistory';
import FinalUserResidualsList from './FinalUserResidualsList';
import ResidualsTable from './ResidualsTable';
import { EmptyResiduals } from './components/EmptyResiduals';
import { useSearchParams } from 'react-router-dom';
import { FINAL_USERS } from '@/enums';

function Residuals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'current';

  const { t } = useTranslation();
  const { company, currentUser } = useGetUser();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: currentReport,
    isLoading: isCurrentReportLoading,
    isError: isCurrentReportError,
  } = useGetCurrentPlatformReportQuery(company?._id ? { companyId: company._id } : skipToken);

  const {
    data: currentReportBilling,
    isLoading: isCurrentReportBillingLoading,
    isError: isCurrentReportBillingError,
  } = useGetPlatformBillingByReportQuery(
    currentReport?._id && view === 'current'
      ? {
          feesReportId: currentReport._id,
          query: {
            searchValue: searchQuery || undefined,
            limit: 25,
            offset: 0,
          },
        }
      : skipToken,
  );

  const isLoading = isCurrentReportLoading || (view === 'current' && isCurrentReportBillingLoading);
  const isError = isCurrentReportError || isCurrentReportBillingError;

  const saveInput = useCallback((input: string) => {
    const lowercasedInput = input.toLowerCase();
    setSearchQuery(lowercasedInput);
  }, []);

  const debouncedSaveInput = debounce(saveInput, 500);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSaveInput(event.target.value);
  };

  const residualsData = useMemo(() => {
    if (!Array.isArray(currentReportBilling)) return [];

    return currentReportBilling.map((item: any) => ({
      _id: item._id || item.id,
      companyName: item.name,
      transactionCount: item.transactionCount || 0,
      paymentVolume: item.totalPaymentAmount || 0,
      totalFee: item.finalPlatformFee || 0,
      finalFee: item.totalFinalFee || 0,
      residualEarned: item.totalResidualApplicationFee || 0,
    }));
  }, [currentReportBilling]);

  const reportCalculations = currentReport
    ? {
        currentResidual: currentReport.residualsEarned || 0,
        transactionCount: currentReport.transactionCount || 0,
        paymentVolume: currentReport.totalPaymentVolume ? currentReport.totalPaymentVolume : 0,
        totalFee: currentReport.totalFeesCollected ? currentReport.totalFeesCollected : 0,
        finalFee: currentReport.totalFinalFee ? currentReport.totalFinalFee : 0,
      }
    : {
        currentResidual: 0,
        transactionCount: 0,
        paymentVolume: 0,
        totalFee: 0,
        finalFee: 0,
      };

  const handleViewChange = (newView: 'current' | 'history') => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('view', newView);
      return newParams;
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <Container size='lg'>
          <Header>
            <Text size='xl' weight='medium'>
              {t('Residuals.title')}
            </Text>
          </Header>
          <SkeletonTable />
        </Container>
      </Layout>
    );
  }

  if (isError || (!isLoading && !currentReport)) {
    return (
      <Layout>
        <Container size='lg'>
          <EmptyResiduals />
        </Container>
      </Layout>
    );
  }

  if (FINAL_USERS.includes(currentUser.type)) {
    return (
      <Layout>
        <Container size='lg'>
          <Text size='xl' weight='medium'>
            {t('Residuals.title')}
          </Text>
          <FinalUserResidualsList />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container size='lg'>
        <Header>
          <Text size='xl' weight='medium'>
            {t('Residuals.title')}
          </Text>
        </Header>
        <div className={styles.buttonGroup}>
          <Button
            variant={view === 'current' ? 'secondary' : 'outline'}
            size='medium'
            onClick={() => handleViewChange('current')}
          >
            {t('Residuals.current_residual_button')}
          </Button>
          <Button
            variant={view === 'history' ? 'secondary' : 'outline'}
            size='medium'
            onClick={() => handleViewChange('history')}
          >
            {t('Residuals.previous_residual_button')}
          </Button>
        </div>
        <Divider />
        {view === 'current' && (
          <>
            {isCurrentReportBillingLoading ? (
              <div>
                <Text size='s' weight='light-medium'>
                  {t('Residuals.loading') || 'Loading...'}
                </Text>
              </div>
            ) : (
              <>
                <CurrentResiduals
                  reportCalculations={reportCalculations}
                  handleSearch={handleSearch}
                  hasResiduals={residualsData.length > 0}
                />
                {residualsData.length > 0 ? (
                  <ResidualsTable residuals={residualsData} />
                ) : (
                  <EmptyResiduals />
                )}
              </>
            )}
          </>
        )}

        {view === 'history' && <ResidualHistory />}
      </Container>
    </Layout>
  );
}

export default Residuals;
